import { FormDrawer, FormDrawerContent, IJarvisUser, Id, MultiSelectField, isSuccess } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { applicationService, useApplicationRolesQuery } from "../shared/api/applicationService";
import { IRole } from "../shared/types";

export const useApplicationUserRoleDrawer = () => {
	const [user, setUser] = useState<IJarvisUser | null>(null);

	const handleOpen = useCallback((user: IJarvisUser) => setUser(user), []);
	const onClose = useCallback(() => setUser(null), []);

	return {
		open: user !== null,
		user: user,
		handleOpen,
		onClose,
	};
};

interface Props {
	open: boolean;
	onClose: () => void;
	user: IJarvisUser;
	applicationId: string;
}

const getLabel = (role: IRole) => role.name;

export const ApplicationUserRolesDrawer: FC<Props> = ({ open, onClose, applicationId, user }) => {
	const { t } = useTranslation();
	const { data: roles } = useApplicationRolesQuery(applicationId);
	const queryClient = useQueryClient();

	const updateApplicationUserRoles = useCallback(
		async (roleIds: Id[]) => {
			const response = await applicationService.updateRoles(applicationId, roleIds, [user.id]);

			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("applications") });
				return true;
			}

			return false;
		},
		[applicationId, queryClient, user],
	);

	return (
		<FormDrawer
			open={open}
			onSubmit={(values) => updateApplicationUserRoles(values.roles.map((role) => role.id))}
			initialValues={{ roles: user.roles }}
			onClose={onClose}
			title={t("Roles")}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<MultiSelectField label={t("Roles")} name="roles" options={roles} getLabel={getLabel} />
				</FormDrawerContent>
			)}
		/>
	);
};
